import React, { useState } from 'react';
import { DollarSign, FileText, Download, TrendingUp, AlertCircle } from 'lucide-react';

interface BillingData {
  projectId: string;
  totalHours: number;
  billableHours: number;
  hourlyRate: number;
  totalCost: number;
  budgetAllocated: number;
  budgetRemaining: number;
  profitMargin: number;
}

interface BillingIntegrationProps {
  projectId: string;
  userRole: 'client' | 'vendor' | 'admin';
}

export const BillingIntegration: React.FC<BillingIntegrationProps> = ({ 
  projectId, 
  userRole 
}) => {
  const [billingData] = useState<BillingData>({
    projectId,
    totalHours: 245,
    billableHours: 220,
    hourlyRate: 150,
    totalCost: 33000,
    budgetAllocated: 45000,
    budgetRemaining: 12000,
    profitMargin: 28.5
  });

  const [isExporting, setIsExporting] = useState(false);

  const exportToBilling = async () => {
    setIsExporting(true);
    
    try {
      const response = await fetch('/api/billing/export-onboarding-time', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          projectId,
          timeEntries: billingData
        }),
      });

      if (response.ok) {
        console.log('Time data exported to ArcOS BillingBee successfully');
      }
    } catch (error) {
      console.error('Error exporting to billing system:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const getBudgetStatus = () => {
    const utilizationPercent = ((billingData.budgetAllocated - billingData.budgetRemaining) / billingData.budgetAllocated) * 100;
    
    if (utilizationPercent > 90) return { color: 'text-red-600', status: 'Critical' };
    if (utilizationPercent > 75) return { color: 'text-yellow-600', status: 'Warning' };
    return { color: 'text-[#8A9150]', status: 'Healthy' };
  };

  const budgetStatus = getBudgetStatus();

  if (userRole === 'client') {
    return (
      <div className="p-6 rounded-xl bg-white border border-gray-200 shadow-lg">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <DollarSign className="w-5 h-5 text-[#4267A1] mr-2" />
          Project Investment
        </h3>
        
        <div className="space-y-4">
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-blue-800 text-sm">
              Your Tyler Technologies implementation is progressing within budget. 
              Detailed billing information is available to your project manager.
            </p>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-[#4267A1] mb-1">
                {Math.round((billingData.totalHours / 300) * 100)}%
              </div>
              <div className="text-sm text-gray-600">Project Progress</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-[#8A9150] mb-1">
                {budgetStatus.status}
              </div>
              <div className="text-sm text-gray-600">Budget Status</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 rounded-xl bg-white border border-gray-200 shadow-lg">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <DollarSign className="w-5 h-5 text-[#4267A1]" />
          <h3 className="text-lg font-semibold text-gray-900">
            Billing & Profitability
          </h3>
        </div>
        
        <button
          onClick={exportToBilling}
          disabled={isExporting}
          className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] text-white rounded-lg hover:shadow-lg hover:shadow-[#4267A1]/25 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
        >
          <Download className="w-4 h-4" />
          <span>{isExporting ? 'Exporting...' : 'Export to ArcOS'}</span>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <FileText className="w-4 h-4 text-gray-600" />
            <h4 className="font-medium text-gray-900">Hours Tracked</h4>
          </div>
          <div className="text-2xl font-bold text-[#4267A1] mb-1">
            {billingData.billableHours}
          </div>
          <div className="text-sm text-gray-600">
            of {billingData.totalHours} total hours
          </div>
        </div>

        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <DollarSign className="w-4 h-4 text-gray-600" />
            <h4 className="font-medium text-gray-900">Revenue</h4>
          </div>
          <div className="text-2xl font-bold text-[#8A9150] mb-1">
            ${billingData.totalCost.toLocaleString()}
          </div>
          <div className="text-sm text-gray-600">
            @ ${billingData.hourlyRate}/hour
          </div>
        </div>

        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <TrendingUp className="w-4 h-4 text-gray-600" />
            <h4 className="font-medium text-gray-900">Profit Margin</h4>
          </div>
          <div className="text-2xl font-bold text-purple-600 mb-1">
            {billingData.profitMargin}%
          </div>
          <div className="text-sm text-gray-600">
            Above target (25%)
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="p-4 border border-gray-200 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-gray-900">Budget Utilization</h4>
            <div className="flex items-center space-x-2">
              <AlertCircle className={`w-4 h-4 ${budgetStatus.color}`} />
              <span className={`text-sm font-medium ${budgetStatus.color}`}>
                {budgetStatus.status}
              </span>
            </div>
          </div>
          
          <div className="flex items-center space-x-4 mb-2">
            <div className="flex-1 bg-gray-200 rounded-full h-3">
              <div 
                className={`h-3 rounded-full ${
                  budgetStatus.status === 'Critical' ? 'bg-red-500' :
                  budgetStatus.status === 'Warning' ? 'bg-yellow-500' : 'bg-[#8A9150]'
                }`}
                style={{ 
                  width: `${((billingData.budgetAllocated - billingData.budgetRemaining) / billingData.budgetAllocated) * 100}%` 
                }}
              ></div>
            </div>
            <span className="text-sm font-medium text-gray-900">
              {Math.round(((billingData.budgetAllocated - billingData.budgetRemaining) / billingData.budgetAllocated) * 100)}%
            </span>
          </div>
          
          <div className="flex justify-between text-sm text-gray-600">
            <span>Used: ${(billingData.budgetAllocated - billingData.budgetRemaining).toLocaleString()}</span>
            <span>Remaining: ${billingData.budgetRemaining.toLocaleString()}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button className="p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-left">
            <h5 className="font-medium text-gray-900 mb-1">Generate Invoice</h5>
            <p className="text-sm text-gray-600">Create invoice for completed work</p>
          </button>
          
          <button className="p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-left">
            <h5 className="font-medium text-gray-900 mb-1">Budget Forecast</h5>
            <p className="text-sm text-gray-600">Project completion cost estimate</p>
          </button>
        </div>
      </div>

      <div className="mt-6 text-xs text-gray-500">
        <p>• Billing integration connects to ArcOS BillingBee for automated invoicing</p>
        <p>• Time tracking data automatically flows to WealthOS for margin analysis</p>
        <p>• All financial data is encrypted and SOC2 compliant</p>
      </div>
    </div>
  );
};
